/**
 * The station-volumes table (spec §4c): every bin, at full resolution, in the
 * order the reader asked for.
 *
 * This is the view a quantity surveyor quotes from, so it is a real `<table>`
 * with a `<caption>`, `scope="col"` headers and `aria-sort` — not a grid of
 * divs — every column of figures is `tabular-nums`, units are stated once in
 * the header rather than repeated on every cell, and a bin the worker flagged
 * as low coverage says so in text and glyph, never in colour alone.
 *
 * **The body is windowed.** The spec allows 20 000 bins; rendering them all is
 * ~180 000 DOM nodes and a dock that takes seconds to open. The chrome, the
 * fixed 24 px rows, the window, the hover-without-a-re-render and the walk row
 * are `DockTableFrame`'s, shared with `ClearanceTable` so the two tables in
 * the same dock slot cannot drift apart again (round-3 audit A6).
 *
 * Sorting and filtering are pure functions in
 * `@/lib/stationVolumes/tableModel`; this file wires them to headers, a
 * search box and the `Columns ▾` menu.
 */
import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import {
  DOCK_TABLE_BADGE,
  DOCK_TABLE_CELL,
  DOCK_TABLE_FOOT,
  DOCK_TABLE_ROW_PX,
  DOCK_TABLE_SHRINK,
  DockColumnsMenu,
  DockSortHeader,
  DockTableAction,
  DockTableEmptyRow,
  DockTableFilter,
  DockTableFrame,
  DockTableNa,
  DockTablePad,
  DockTableSearch,
  useDockTableWindow,
} from '@/components/dock/DockTableFrame';
import { VerdictBadge } from '@/components/VerdictChip';
import { binCentreStation, totalsForBins } from '@/lib/stationVolumes/aggregate';
import { coverageBand } from '@/lib/stationVolumes/coverageBands';
import {
  formatM2,
  formatM3Row,
  formatPct,
  formatSigned,
  formatStationRange,
  m3Formatter,
  staOnly,
  STATION_VOLUME_DISPLAY_DECIMALS,
} from '@/lib/stationVolumes/format';
import {
  crossfallFor,
  DEFAULT_SORT,
  endAreaFor,
  isDefaultSort,
  nextSort,
  parseStationQuery,
  sortBins,
  stationMatchesBin,
  type StationSort,
  type StationSortKey,
} from '@/lib/stationVolumes/tableModel';
import type { StationBin, StationVolumesAnalysisKind, StationVolumesJson } from '@/lib/stationVolumes/types';
import { mToMm } from '@/lib/surfaceExpectation';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface StationVolumeTableProps {
  json: StationVolumesJson;
  layerId: string;
  /**
   * Which analysis produced the run — copy only. The "Cells" column counts
   * cells with data on BOTH inputs, and on a deviation run those two inputs
   * are the point cloud and the design model rather than two surfaces.
   */
  kind?: StationVolumesAnalysisKind;
}

interface ColumnSpec {
  key: StationSortKey;
  label: string;
  /** Stated once, here, and never on a cell. */
  unit?: string;
  help?: string;
  align: 'left' | 'right';
}

export const StationVolumeTable = ({ json, layerId, kind = 'surfaceDifference' }: StationVolumeTableProps) => {
  const { t } = useTranslation('project');
  /** The one column whose explanation names the two compared inputs. */
  const cellsHelp = t(
    kind === 'deviation' ? 'stationVolumes.table.columnCellsHelpDeviation' : 'stationVolumes.table.columnCellsHelp',
  );
  /** What a low-coverage flag was measured against: the reference surface's
   *  footprint on a current run, the whole swath on an older one. */
  const lowCoverageKey =
    json.params.coverageBasis === 'REFERENCE_SURFACE'
      ? 'stationVolumes.table.lowCoverageRef'
      : 'stationVolumes.table.lowCoverage';
  const selection = useAtomValue(stationVolumeSelectionAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [sorts, setSorts] = useState<StationSort[]>(DEFAULT_SORT);
  const [query, setQuery] = useState('');
  const [onlySelection, setOnlySelection] = useState(false);
  const [showAea, setShowAea] = useState(false);
  const [showCells, setShowCells] = useState(false);
  const [showCrossfall, setShowCrossfall] = useState(false);

  const sel = selection?.layerId === layerId ? selection : null;
  const selLo = sel ? Math.min(sel.fromBin, sel.toBin) : null;
  const selHi = sel ? Math.max(sel.fromBin, sel.toBin) : null;

  const parsedQuery = useMemo(() => parseStationQuery(query), [query]);
  const queryUnparseable = query.trim().length > 0 && parsedQuery == null;

  // The brush bounds only enter the row list while "Only selection" is on —
  // otherwise a brush drawn or cleared would hand the window a new row order
  // and scroll the table back to the top under the reader.
  const filterLo = onlySelection ? selLo : null;
  const filterHi = onlySelection ? selHi : null;
  const rows = useMemo(() => {
    let base = json.bins;
    if (filterLo != null && filterHi != null) {
      base = base.filter((b) => b.i >= filterLo && b.i <= filterHi);
    }
    if (parsedQuery != null) base = base.filter((b) => stationMatchesBin(b, parsedQuery));
    return sortBins(base, sorts, json.stations);
  }, [json.bins, json.stations, filterLo, filterHi, parsedQuery, sorts]);

  /** Sums exactly the rows on screen, and the footer says so — mixing a
   *  filtered subtotal with a corridor total is how a figure gets mis-quoted. */
  const totals = useMemo(() => totalsForBins(rows), [rows]);
  const [cutTotal, fillTotal, netTotal] = formatM3Row(
    [totals.cut, totals.fill, totals.net],
    STATION_VOLUME_DISPLAY_DECIMALS,
  );

  /** One fixed precision across the detailed volume block. Chosen from every
   * bin rather than the filtered rows, so searching or brushing cannot change
   * the presentation under the reader. */
  const fmt = useMemo(() => m3Formatter(json.bins, STATION_VOLUME_DISPLAY_DECIMALS), [json.bins]);
  const volumeCells = useMemo(() => {
    const byBin = new Map<number, { cut: string; fill: string; net: string; cumulative: string }>();
    for (const b of json.bins) {
      byBin.set(b.i, {
        cut: fmt.volume(b.cutVolumeM3),
        fill: fmt.volume(b.fillVolumeM3),
        net: fmt.volume(b.netVolumeM3),
        cumulative: fmt.cumulative(b.cumulativeNetM3),
      });
    }
    return byBin;
  }, [json.bins, fmt]);

  /** The bin the walk cursor is standing in, if it is on this alignment. */
  const walkBinIndex = useMemo(() => {
    if (!walk || walk.key !== json.alignment.key) return null;
    const hit = json.bins.find(
      (b, idx) =>
        walk.station >= b.staStartInternalM &&
        (walk.station < b.staEndInternalM || (idx === json.bins.length - 1 && walk.station <= b.staEndInternalM)),
    );
    return hit ? hit.i : null;
  }, [walk, json.bins, json.alignment.key]);

  // Every column carries a `help` sentence. Two figures on this table are
  // routinely misread and both explanations are load-bearing: cut/fill are
  // VOLUMES over an interval while End area is an AREA at a chainage (m³ vs
  // m²), and AEA is a second m³ for the same interval arrived at a different
  // way. A reader who cannot tell those apart cannot check any of them.
  // One sentence, two places: the column header and the checkbox that reveals
  // the column. Deriving both from here is what stops them drifting apart.
  const aeaHelp = json.stations
    ? t('stationVolumes.table.columnAeaAtStationHelp')
    : t('stationVolumes.table.columnAeaHelp');

  // Run-level, not a user toggle like AEA/Cells: the column exists only when
  // the run itself carried an expectation, which `meanThicknessM` being
  // PRESENT (even as `null`, for a bin that measured nothing) on at least one
  // bin tells us — absent entirely is how a NONE run looks (Task 6).
  const hasThickness = useMemo(() => json.bins.some((b) => b.meanThicknessM !== undefined), [json.bins]);

  // Run-level like the thickness column, but behind a toggle like AEA/Cells:
  // the table is already seven columns wide by default and this adds two.
  // Gated on `params.crossfall` — the RUN carried a check — never on any
  // station line having resolved one, so a corridor the fit could not resolve
  // anywhere still says so instead of silently losing the columns.
  const hasCrossfall = json.params.crossfall != null;
  const crossfallHelp = hasCrossfall
    ? t('stationVolumes.table.columnCrossfallHelp', { tol: json.params.crossfall!.tolerancePct })
    : '';

  const columns: ColumnSpec[] = [
    {
      key: 'station',
      label: t('stationVolumes.table.station'),
      help: t('stationVolumes.table.columnStationHelp'),
      align: 'left',
    },
    {
      key: 'cut',
      label: t('stationVolumes.panel.cut'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnCutHelp'),
      align: 'right',
    },
    {
      key: 'fill',
      label: t('stationVolumes.panel.fill'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnFillHelp'),
      align: 'right',
    },
    {
      key: 'net',
      label: t('stationVolumes.panel.net'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnNetHelp'),
      align: 'right',
    },
    {
      key: 'cumulative',
      label: t('stationVolumes.chart.cumulative'),
      unit: t('stationVolumes.panel.units'),
      help: t('stationVolumes.table.columnCumulativeHelp'),
      align: 'right',
    },
    {
      key: 'coverage',
      label: t('stationVolumes.panel.coverage'),
      unit: t('stationVolumes.table.unitsPct'),
      help: t(
        json.params.coverageBasis === 'REFERENCE_SURFACE'
          ? kind === 'deviation'
            ? 'stationVolumes.table.columnCoverageHelpRefDeviation'
            : 'stationVolumes.table.columnCoverageHelpRef'
          : 'stationVolumes.table.columnCoverageHelp',
        { min: json.params.minCoveragePct },
      ),
      align: 'right',
    },
    {
      key: 'endArea',
      // With true station-line sections the column is the area AT a chainage,
      // not a mean over the interval, and the header has to say which.
      label: json.stations
        ? t('stationVolumes.table.endAreaAtStation')
        : t('stationVolumes.chart.endArea'),
      unit: t('stationVolumes.table.unitsArea'),
      help: json.stations
        ? t('stationVolumes.table.endAreaAtStationHelp')
        : t('stationVolumes.table.endAreaHelp'),
      align: 'right',
    },
    ...(hasThickness
      ? [
          {
            key: 'thickness' as const,
            label: t('stationVolumes.table.columnThickness'),
            help: t('stationVolumes.table.columnThicknessHelp'),
            align: 'right' as const,
          },
        ]
      : []),
    ...(showAea
      ? [
          {
            key: 'aea' as const,
            label: t('stationVolumes.table.columnAea'),
            unit: t('stationVolumes.panel.units'),
            // With station lines the AEA is the mean of two REAL end areas;
            // without them it is the mean of two interval means. Different
            // derivations, so different sentences.
            help: aeaHelp,
            align: 'right' as const,
          },
        ]
      : []),
    ...(showCells
      ? [
          {
            key: 'cells' as const,
            label: t('stationVolumes.table.columnCells'),
            help: cellsHelp,
            align: 'right' as const,
          },
        ]
      : []),
    ...(hasCrossfall && showCrossfall
      ? (['left', 'right'] as const).map((side) => ({
          key: (side === 'left' ? 'crossfallL' : 'crossfallR') as const,
          label: t(
            side === 'left' ? 'stationVolumes.table.columnCrossfallL' : 'stationVolumes.table.columnCrossfallR',
          ),
          unit: t('stationVolumes.table.unitsPct'),
          help: crossfallHelp,
          align: 'right' as const,
        }))
      : []),
  ];

  const colCount = columns.length;

  const win = useDockTableWindow({
    rows,
    keyOf: (b: StationBin) => b.i,
    // Driven from the chart, the 3D bands or the walk toolbar rather than
    // from this table — revealed only when the walk itself moves.
    revealKey: walkBinIndex,
    measureKey: colCount,
  });

  // ----------------------------------------------------------- interaction
  const walkTo = (bin: StationBin) => {
    // `follow` and `step` are the user's settings on the walk toolbar; a row
    // click moves the cursor, it does not take over the camera.
    setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station: binCentreStation(bin) } : w));
    win.setFocusedKey(bin.i);
  };

  const onRowKeyDown = (bin: StationBin) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      walkTo(bin);
      return;
    }
    const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    win.moveFocus(bin.i, step);
  };

  const sortIndex = (key: StationSortKey) => sorts.findIndex((s) => s.key === key);

  // One tab stop into the rows, not one per row: 20 000 rows in the tab order
  // is a keyboard trap by another name. The stop follows the last row the
  // reader arrowed to, then the walk cursor, then the top of the table — so
  // tabbing out and back in returns you where you were.
  const rovingBin = win.rovingKey(walkBinIndex);

  return (
    <DockTableFrame
      scrollRef={win.scrollRef}
      onScroll={win.onScroll}
      scrollTestId='sv-table-scroll'
      // The interaction hint as the viewport's tooltip, as on the clearance
      // table: the dock is narrow, and the contract is learnt once.
      scrollTitle={t('stationVolumes.table.hint')}
      caption={t('stationVolumes.table.caption', {
        bins: json.bins.length,
        from: staOnly(json.bins[0]?.staStartDisplayM ?? 0),
        to: staOnly(json.bins[json.bins.length - 1]?.staEndDisplayM ?? 0),
      })}
      toolbar={
        <>
          <DockTableSearch
            label={t('stationVolumes.table.searchSta')}
            value={query}
            onChange={setQuery}
            placeholder={t('stationVolumes.table.searchStaPlaceholder')}
            clearLabel={t('stationVolumes.table.searchStaClear')}
            invalid={queryUnparseable}
            errorText={t('stationVolumes.errors.stationUnparseable')}
            testId='sv-table-search'
            clearTestId='sv-table-search-clear'
          />
          {sel && (
            <DockTableFilter
              testId='sv-table-only-selection'
              label={t('stationVolumes.table.onlySelection')}
              checked={onlySelection}
              onCheckedChange={setOnlySelection}
            />
          )}
        </>
      }
      columns={
        <DockColumnsMenu
          testId='sv-table-columns'
          options={[
            {
              key: 'aea',
              label: t('stationVolumes.table.columnAea'),
              title: aeaHelp,
              checked: showAea,
              onCheckedChange: setShowAea,
              testId: 'sv-table-col-aea',
            },
            {
              key: 'cells',
              label: t('stationVolumes.table.columnCells'),
              title: cellsHelp,
              checked: showCells,
              onCheckedChange: setShowCells,
              testId: 'sv-table-col-cells',
            },
            ...(hasCrossfall
              ? [
                  {
                    key: 'crossfall',
                    label: t('stationVolumes.table.columnCrossfall'),
                    title: crossfallHelp,
                    checked: showCrossfall,
                    onCheckedChange: setShowCrossfall,
                    testId: 'sv-table-col-crossfall',
                  },
                ]
              : []),
          ]}
        />
      }
      actions={
        !isDefaultSort(sorts) && (
          <DockTableAction testId='sv-table-reset-order' onClick={() => setSorts(DEFAULT_SORT)}>
            {t('stationVolumes.table.resetOrder')}
          </DockTableAction>
        )
      }>
      <thead>
        <tr>
          {columns.map((col) => {
            const at = sortIndex(col.key);
            return (
              <DockSortHeader
                key={col.key}
                testId={`sv-th-${col.key}`}
                label={col.label}
                unit={col.unit}
                help={col.help}
                sortAt={at}
                dir={at >= 0 ? sorts[at].dir : null}
                onSort={(shift) => setSorts((current) => nextSort(current, col.key, shift))}
                align={col.align}
                shrink={col.key === 'station'}
              />
            );
          })}
        </tr>
      </thead>

      <tbody ref={win.bodyRef}>
        {/* A station that matches nothing is the only empty state this
            body has — an unparseable one filters nothing, so it can never
            produce "No bins match ." */}
        {rows.length === 0 && parsedQuery != null && (
          <DockTableEmptyRow colSpan={colCount} testId='sv-table-empty'>
            {t('stationVolumes.table.noMatch', { station: staOnly(parsedQuery) })}
          </DockTableEmptyRow>
        )}
        <DockTablePad height={win.padTopPx} colSpan={colCount} testId='sv-pad-top' />
        {win.windowRows.map((b) => {
          const inSelection = selLo == null || selHi == null || (b.i >= selLo && b.i <= selHi);
          const walked = walkBinIndex === b.i;
          const cells = volumeCells.get(b.i);
          return (
            <tr
              key={b.i}
              data-bin={b.i}
              data-testid={`sv-row-${b.i}`}
              // Focus is an OUTLINE, not a ring: a ring is a box-shadow,
              // and a box-shadow on a row of a `border-collapse` table is
              // not painted — the focus would be invisible exactly where
              // a keyboard user needs it.
              className='sv-row cursor-pointer border-b border-[var(--sv-grid)] focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-500'
              tabIndex={rovingBin === b.i ? 0 : -1}
              aria-current={walked ? 'location' : undefined}
              style={{
                height: DOCK_TABLE_ROW_PX,
                // 55 % outside the brush: still readable, plainly not the
                // range the header's totals are talking about.
                opacity: inSelection ? undefined : 0.55,
              }}
              onMouseEnter={() => setHover(b.i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => win.setFocusedKey(b.i)}
              onClick={() => walkTo(b)}
              onKeyDown={onRowKeyDown(b)}>
              <td className={`sv-sta text-left ${DOCK_TABLE_CELL} ${DOCK_TABLE_SHRINK}`}>{formatStationRange(b)}</td>
              <td className={DOCK_TABLE_CELL} style={{ color: 'var(--sv-cut)' }}>
                {cells?.cut}
              </td>
              <td className={DOCK_TABLE_CELL} style={{ color: 'var(--sv-fill)' }}>
                {cells?.fill}
              </td>
              <td className={DOCK_TABLE_CELL}>{cells?.net}</td>
              <td className={DOCK_TABLE_CELL}>{cells?.cumulative}</td>
              <td className={DOCK_TABLE_CELL}>
                {/* A plain figure, tinted only when the bin is LOW (audit
                    A6): a coloured chip on every row made a column of
                    green pills that said nothing and fought the walk
                    tint. The five bands still draw the chart's coverage
                    strip, and ride here as `data-band`. The ⚠ stays: it
                    is what survives a greyscale print. */}
                <span
                  data-testid='sv-coverage-cell'
                  data-band={coverageBand(b.coveragePct).index}
                  data-low={b.lowCoverage || undefined}
                  className={b.lowCoverage ? 'font-medium' : undefined}
                  style={b.lowCoverage ? { color: 'var(--sv-warn)' } : undefined}>
                  {formatPct(b.coveragePct)}
                </span>
                {b.lowCoverage && (
                  <span
                    title={t(lowCoverageKey, { min: json.params.minCoveragePct })}
                    style={{ color: 'var(--sv-warn)' }}>
                    <span aria-hidden> ⚠</span>
                    <span className='sr-only'>
                      {` ${t(lowCoverageKey, { min: json.params.minCoveragePct })}`}
                    </span>
                  </span>
                )}
              </td>
              <td className={DOCK_TABLE_CELL}>
                {(() => {
                  const a = endAreaFor(b, json.stations);
                  return `${formatM2(a.cut)} / ${formatM2(a.fill)}`;
                })()}
              </td>
              {/* One cell, not two: this table renders exactly one `<td>`
                  per `columns` entry (no colSpan machinery), so a
                  separately-headed "Verdict" column would need its own
                  `<th>` — but the verdict has no sort of its own (only
                  `thickness` does), and `sv-th-thickness` is the only
                  header the brief's tests ask for. The badge rides in the
                  same cell as the figure it judges instead; it is still
                  the non-interactive badge Task 14 calls for, never the
                  openable `VerdictChip` (that belongs once, corridor-wide,
                  in the dock header — Step 3b). */}
              {hasThickness && (
                <td
                  data-testid='sv-bin-verdict'
                  data-verdict={b.verdict}
                  title={b.verdict ? t(`expectation.verdictHelp.${b.verdict}`) : undefined}
                  // Uncoloured, as on the clearance table: a filled cell
                  // turned a failing run's column into one solid stripe
                  // and fought the walk tint on the walked row. The
                  // colour rides on the badge instead.
                  className={DOCK_TABLE_CELL}>
                  {b.meanThicknessM == null ? (
                    <span title={t('stationVolumes.table.thicknessNa')}>
                      <span aria-hidden>—</span>
                      <span className='sr-only'>{t('stationVolumes.table.thicknessNa')}</span>
                    </span>
                  ) : (
                    mToMm(b.meanThicknessM).toFixed(1)
                  )}
                  {/* The house badge, shrunk to the row's density — the
                      same treatment as `ClearanceTable`'s verdict cell.
                      Its label is printed, so it needs no sr-only twin. */}
                  {b.verdict && (
                    <>
                      {' '}
                      <VerdictBadge verdict={b.verdict} className={DOCK_TABLE_BADGE} />
                    </>
                  )}
                </td>
              )}
              {showAea && (
                <td className={DOCK_TABLE_CELL}>
                  {b.cutVolumeAeaM3 == null || b.fillVolumeAeaM3 == null ? (
                    <DockTableNa text={t('stationVolumes.table.aeaNa')} />
                  ) : (
                    `${fmt.volume(b.cutVolumeAeaM3)} / ${fmt.volume(b.fillVolumeAeaM3)}`
                  )}
                </td>
              )}
              {showCells && <td className={DOCK_TABLE_CELL}>{b.cellCount}</td>}
              {hasCrossfall &&
                showCrossfall &&
                (['left', 'right'] as const).map((side) => {
                  const c = crossfallFor(b, json.stations, side);
                  // Three absences print the same em dash, but they do
                  // not have the same cause: a side the RUN never
                  // configured is a whole column of dashes, and "the fit
                  // could not resolve this band" would blame a fit nobody
                  // asked for.
                  const hasBand = json.params.crossfall?.[side] != null;
                  const naText = t(
                    hasBand ? 'stationVolumes.table.crossfallNa' : 'stationVolumes.table.crossfallNoBand',
                  );
                  return (
                    <td
                      key={side}
                      data-testid={`sv-crossfall-${side}-cell`}
                      data-verdict={c?.verdict}
                      title={c?.reason ? t(`stationVolumes.table.crossfallReason.${c.reason}`) : undefined}
                      // Uncoloured, badge-carried verdict — the same
                      // treatment as the thickness cell above.
                      className={DOCK_TABLE_CELL}>
                      {c?.deltaPct == null || c.asbuiltPct == null ? (
                        <span title={naText}>
                          <span aria-hidden>—</span>
                          <span className='sr-only'>{naText}</span>
                        </span>
                      ) : (
                        // "as-built (Δ)", the shape the spec asks for.
                        // `formatSigned` keeps the U+2212 minus the rest
                        // of this dock prints, so a negative crossfall
                        // does not read as a hyphen.
                        `${formatSigned(c.asbuiltPct, 2)} (${formatSigned(c.deltaPct, 2)})`
                      )}
                      {c?.verdict && (
                        <>
                          {' '}
                          <VerdictBadge verdict={c.verdict} className={DOCK_TABLE_BADGE} />
                        </>
                      )}
                    </td>
                  );
                })}
            </tr>
          );
        })}
        <DockTablePad height={win.padBottomPx} colSpan={colCount} testId='sv-pad-bottom' />
      </tbody>

      <tfoot className={DOCK_TABLE_FOOT}>
        <tr>
          <th scope='row' data-testid='sv-table-total-label' className={`text-left font-semibold ${DOCK_TABLE_CELL}`}>
            {t('stationVolumes.table.totalsShown', { bins: rows.length })}
          </th>
          <td className={DOCK_TABLE_CELL} data-testid='sv-table-total-cut'>
            {cutTotal}
          </td>
          <td className={DOCK_TABLE_CELL} data-testid='sv-table-total-fill'>
            {fillTotal}
          </td>
          <td className={DOCK_TABLE_CELL} data-testid='sv-table-total-net'>
            {netTotal}
          </td>
          {/* Cumulative, coverage and the areas have no meaningful total —
              an empty cell says that better than a number nobody can use. */}
          <td colSpan={colCount - 4} className={DOCK_TABLE_CELL} />
        </tr>
      </tfoot>
    </DockTableFrame>
  );
};
